import { isNonNullable, Opt } from 'app/tools/general.types';
import { useObservableState } from 'observable-hooks';
import React, { useContext } from 'react';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

type FramePortalHook = [
  frameRef: HTMLElement | null,
  setFrameRef: (ref: HTMLElement | null) => unknown
];
const OuterFramePortalContext =
  React.createContext<Opt<FramePortalHook>>(undefined);
const InnerFramePortalContext =
  React.createContext<Opt<FramePortalHook>>(undefined);

type Props = {
  children: React.ReactNode;
};

export function FramePortalProvider({
  children,
}: Props): JSX.Element {
  const [innerFrameRef, setInnerFrameRef] = useObservableState(
    (ref$: Observable<HTMLElement | null>) =>
      ref$.pipe(filter(isNonNullable)),
    null
  );
  const [outerFrameRef, setOuterFrameRef] = useObservableState(
    (ref$: Observable<HTMLElement | null>) =>
      ref$.pipe(filter(isNonNullable)),
    null
  );

  return (
    <OuterFramePortalContext.Provider
      value={[outerFrameRef, setOuterFrameRef]}
    >
      <InnerFramePortalContext.Provider
        value={[innerFrameRef, setInnerFrameRef]}
      >
        {children}
      </InnerFramePortalContext.Provider>
    </OuterFramePortalContext.Provider>
  );
}

export const useInnerFramePortal = (): FramePortalHook => {
  const framePortalHook = useContext(InnerFramePortalContext);
  if (framePortalHook === undefined) {
    throw new Error(
      'useInnerFramePortal can only be used inside its provider'
    );
  }
  return framePortalHook;
};

export const useOuterFramePortal = (): FramePortalHook => {
  const framePortalHook = useContext(OuterFramePortalContext);
  if (framePortalHook === undefined) {
    throw new Error(
      'useOuterFramePortal can only be used inside its provider'
    );
  }
  return framePortalHook;
};
